import { useState, type FormEvent } from "react";
import SectionHeading from "./SectionHeading";

const FORM_URL = "https://readdy.ai/api/form/daf7kshtto0ceqqnh770";

const subjects = ["纠错/申请下线", "线索与素材投稿", "媒体合作", "其他"];

export default function ContactSection() {
  const [status, setStatus] = useState<"idle" | "sending" | "success">("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // 反垃圾蜜罐
    const honeypot = String(fd.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      setFormError("");
      return;
    }

    const payload = new FormData();
    const subject = String(fd.get("subject") || "");
    const name = String(fd.get("name") || "").trim();
    const email = String(fd.get("email") || "").trim();
    const message = String(fd.get("message") || "").trim();
    if (subject) payload.append("意向类型", subject);
    if (name) payload.append("称呼", name);
    if (email) payload.append("email", email);
    if (message) payload.append("留言内容", message);

    setStatus("sending");
    setFormError("");
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      if (!res.ok || !parsed || parsed.code !== "OK") {
        const msg =
          serverMsg && serverMsg.toLowerCase().includes("spam")
            ? "提交内容未能通过审核，请重新填写。"
            : serverMsg || "提交失败，请稍后再试。";
        setFormError(msg);
        setStatus("idle");
        return;
      }
      setStatus("success");
      form.reset();
    } catch {
      setFormError("网络异常，请稍后再试。");
      setStatus("idle");
    }
  }

  const inputCls =
    "w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200";

  return (
    <section id="contact" className="relative overflow-hidden bg-background-50 py-14 md:py-20">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(circle at 8% 15%, oklch(var(--accent-500)/0.18), transparent 32%), radial-gradient(circle at 95% 90%, oklch(var(--primary-500)/0.12), transparent 34%)",
        }}
      />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 md:px-6 lg:grid-cols-2">
        <div>
          <SectionHeading
            index="06"
            en="Report & Co-op"
            title="纠错、下线还是来投稿？"
            desc="看到物料对不上、想来份素材，或想谈媒体合作，都可以在右边留下线索与可查证的来源链接。我们会在 24 小时内人工复核回复。"
          />
          <div className="mt-6 space-y-3 text-sm text-foreground-600">
            <p className="flex items-center gap-2">
              <i className="ri-mail-send-line text-primary-600" />
              投稿邮箱：
              <a href="mailto:hello@chiguafancha.example" className="font-semibold text-primary-700 hover:underline">
                hello@chiguafancha.example
              </a>
            </p>
            <p className="flex items-center gap-2">
              <i className="ri-focus-3-fill text-primary-600" />
              只接收「公开可查证」的素材与来源链接
            </p>
            <p className="flex items-center gap-2">
              <i className="ri-shield-check-line text-secondary-700" />
              尊重当事人，涉及私生活的主观猜测一律不收
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-background-200/80 bg-background-100/80 p-5 md:p-6">
          {status === "success" ? (
            <div className="flex h-full flex-col items-center justify-center py-12 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className="ri-check-line text-2xl" />
              </span>
              <h3 className="mt-4 font-heading text-xl font-bold text-foreground-950">
                收到啦，谢谢你！
              </h3>
              <p className="mt-2 max-w-sm text-sm leading-relaxed text-foreground-600">
                我们会尽快人工复核你的线索。若符合「公开可查证」标准，将补充进对应档案并注明贡献。
              </p>
              <button
                type="button"
                onClick={() => setStatus("idle")}
                className="mt-6 rounded-md bg-foreground-950 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-foreground-800"
              >
                再写一条
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-readdy-form="chigua-contact" className="space-y-4">
              {/* 反垃圾蜜罐 */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-honeypot"
              />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-xs font-semibold text-foreground-700">
                    你的称呼
                  </label>
                  <input id="name" name="name" required placeholder="怎么称呼你" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-xs font-semibold text-foreground-700">
                    联系邮箱
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="name@example.com"
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="mb-1.5 block text-xs font-semibold text-foreground-700">
                  意向类型
                </label>
                <select id="subject" name="subject" className={inputCls}>
                  {subjects.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="message" className="mb-1.5 block text-xs font-semibold text-foreground-700">
                  留言 / 素材来源链接
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  required
                  maxLength={500}
                  placeholder="描述想反馈的内容，并附上可查证的公开来源链接，最多 500 字。"
                  className={`${inputCls} resize-none`}
                />
                <p className="mt-1 text-right text-[11px] text-foreground-400">限 500 字</p>
              </div>

              {formError && (
                <div className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2.5 text-sm text-primary-800">
                  <i className="ri-error-warning-line mt-0.5" />
                  {formError}
                </div>
              )}

              <button
                type="submit"
                disabled={status === "sending"}
                className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
              >
                {status === "sending" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    提交中…
                  </>
                ) : (
                  <>
                    提交线索
                    <i className="ri-send-plane-line" />
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}